import { Suspense, lazy, useEffect, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from './router';
import { telemetry } from '@/lib/telemetry/bus';
import type { SkyboxSlug } from '@/lib/three-bridge/skybox';
import type { GameId } from '@/lib/types';
import { GAMES } from '@/lib/types';
import { getScores } from '@/lib/store/scores';
import { abandonBattery, getBattery, markGameCompleted, startBattery } from '@/lib/store/battery';

// v6: 5-screen flow — home(dashboard) → play → cleared → report. The
// briefing/instructions/calibration/welcome/loading fiction screens are
// retired; calibration runs in the background inside PlayScene.
const DashboardScene = lazy(() => import('@/screens/DashboardScene').then((m) => ({ default: m.DashboardScene })));
const PlayScene = lazy(() => import('@/screens/PlayScene').then((m) => ({ default: m.PlayScene })));
const ClearedScene = lazy(() => import('@/screens/ClearedScene').then((m) => ({ default: m.ClearedScene })));
const ReportScene = lazy(() => import('@/screens/ReportScene').then((m) => ({ default: m.ReportScene })));

function skyboxFor(screen: string, game?: GameId): SkyboxSlug {
  // Per-game skyboxes when a game context is known; otherwise per-screen
  // ambient choices that match each prototype's dominant world. tracking-dome
  // (cyan) is the most neutral and matches prototype cyan/teal palettes;
  // we lean on it for the analytical / overview screens.
  if (game) return game as SkyboxSlug;
  if (screen === 'dashboard') return 'tracking';
  if (screen === 'report') return 'tracking';
  return 'tracking'; // welcome
}

export function App() {
  const { screen, params, navigate } = useRouter();

  const nextAssessmentGame = () => GAMES.find((g) => getScores()[g]?.score == null);
  const continueAssessment = () => {
    // Battery-aware: pull the next game from battery state; fall back to
    // scores-derived next if the battery has been abandoned or never started.
    const battery = getBattery();
    if (battery.status !== 'in_progress') startBattery();
    const next = nextAssessmentGame();
    if (next) navigate('play', { game: next, mode: 'full' });   // straight into the game
    else navigate('report');                                    // battery done → report
  };

  const recordBatteryCompletion = (game: GameId) => {
    const battery = getBattery();
    if (battery.status === 'in_progress') markGameCompleted(game);
  };

  const abandonAndReport = () => {
    abandonBattery();
    navigate('report');
  };

  useEffect(() => {
    telemetry.emit({ type: 'app.screen.enter', screen, ts: performance.now() });
    const slug = skyboxFor(screen, params.game);
    const timer = window.setTimeout(() => {
      void import('@/lib/three-bridge/skybox').then(({ setSkybox }) => setSkybox(slug));
    }, 0);
    document.body.dataset.screen = screen;
    return () => {
      window.clearTimeout(timer);
      telemetry.emit({ type: 'app.screen.exit', screen, ts: performance.now() });
    };
  }, [screen, params.game]);

  // play/cleared require ?game=… — fall back to home otherwise.
  const goHome = () => navigate('home');
  const playGame = (g: GameId, mode: 'full' | 'single') => navigate('play', { game: g, mode });

  let scene: ReactNode = null;
  switch (screen) {
    case 'play':
      if (!params.game) { scene = home(); break; }
      scene = <PlayScene game={params.game} difficulty={params.seed ?? 3} onCleared={(metrics) => { recordBatteryCompletion(params.game!); navigate('cleared', { ...params, metrics }); }} />;
      break;
    case 'cleared':
      if (!params.game) { scene = home(); break; }
      scene = <ClearedScene game={params.game} metrics={params.metrics} onRetry={() => playGame(params.game!, params.mode ?? 'single')} onContinue={() => params.mode === 'full' ? continueAssessment() : goHome()} onMap={() => params.mode === 'full' ? abandonAndReport() : goHome()} />;
      break;
    case 'report':
      scene = <ReportScene onBack={goHome} />;
      break;
    default: // 'home' (and any legacy screen) → the 6-game grid
      scene = home();
  }
  // Cross-fade between screens for a smooth handoff (respects the global
  // reduced-motion config in main.tsx).
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={`${screen}:${params.game ?? ''}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        style={{ height: '100%' }}
      >
        <Suspense fallback={null}>{scene}</Suspense>
      </motion.div>
    </AnimatePresence>
  );

  function home() {
    return (
      <DashboardScene
        onPick={(g) => playGame(g, 'single')}
        onReport={() => navigate('report')}
        onFullStart={continueAssessment}
      />
    );
  }
}
